import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import reportWebVitals from './reportWebVitals';

const isPublicFormRoute =
  typeof window !== 'undefined' && window.location.pathname.startsWith('/form/');

// Public embedded form pages should not expose internal debug/trace logs.
if (typeof window !== 'undefined' && isPublicFormRoute) {
  // Keep errors visible; silence noisy diagnostics only.
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
}

// IMPORTANT: Set up error handlers IMMEDIATELY after imports
// This ensures we catch ToastUI errors before React's error overlay intercepts them
if (typeof window !== 'undefined') {
  // Store original handlers
  const originalConsoleError = console.error;
  const originalOnError = window.onerror;
  const originalOnUnhandledRejection = window.onunhandledrejection;

  // Override console.error FIRST - before anything else
  console.error = function(...args: any[]) {
    const errorString = args.join(' ');
    if (
      errorString.includes('fire') ||
      errorString.includes('CustomEvents') ||
      errorString.includes('Cannot read properties of null') ||
      errorString.includes("reading 'fire'") ||
      errorString.includes('tui-image-editor') ||
      errorString.includes('toast-ui') ||
      errorString.includes('vendors-node_modules_toast-ui') ||
      errorString.includes('unlayer')
    ) {
      // Completely suppress
      return;
    }
    originalConsoleError.apply(console, args);
  } as typeof console.error;

  // Override window.onerror FIRST - this catches errors before event listeners
  window.onerror = function(message, source, lineno, colno, error) {
    const errorMessage = String(message || '');
    const errorStack = error?.stack || '';
    const sourceString = String(source || '');
    
    if (
      errorMessage.includes('fire') ||
      errorMessage.includes('CustomEvents') ||
      errorMessage.includes('Cannot read properties of null') ||
      errorMessage.includes("reading 'fire'") ||
      errorStack.includes('CustomEvents') ||
      errorStack.includes('tui-image-editor') ||
      errorStack.includes('toast-ui') ||
      errorStack.includes('vendors-node_modules_toast-ui') ||
      sourceString.includes('toast-ui') ||
      sourceString.includes('tui-image-editor')
    ) {
      return true; // Suppress completely
    }
    
    if (originalOnError) {
      return originalOnError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };

  // Override window.onunhandledrejection FIRST
  window.onunhandledrejection = function(event) {
    const reason = event.reason;
    const errorMessage = reason?.message || String(reason) || '';
    const errorStack = reason?.stack || '';
    
    if (
      errorMessage.includes('fire') ||
      errorMessage.includes('CustomEvents') ||
      errorMessage.includes('Cannot read properties of null') ||
      errorMessage.includes("reading 'fire'") ||
      errorStack.includes('CustomEvents') ||
      errorStack.includes('tui-image-editor') ||
      errorStack.includes('toast-ui') ||
      errorStack.includes('vendors-node_modules_toast-ui')
    ) {
      event.preventDefault();
      return true;
    }
    
    if (originalOnUnhandledRejection) {
      return originalOnUnhandledRejection.call(window, event);
    }
    return false;
  };
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Check if we're in admin mode (separate deployment or admin subdomain)
const isAdminMode = process.env.REACT_APP_ADMIN_MODE === 'true';
const isAdminSubdomain = window.location.hostname.startsWith('admin.');

// Global error handler for third-party library errors (ToastUI, Unlayer)
// These libraries have React version compatibility issues but still work
const setupGlobalErrorHandlers = () => {
  // Override console.error to catch and suppress these specific errors
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    // SILENCING DISABLED FOR EMERGENCY DEBUGGING
    originalConsoleError.apply(console, args);
  };

  const handleUnhandledError = (event: ErrorEvent) => {
    const errorMessage = event.error?.message || event.message || '';
    const errorStack = event.error?.stack || '';
    const errorName = event.error?.name || '';
    
    // Suppress "undefined" errors that occur during template editing
    if (errorMessage === 'undefined' || errorMessage === '' || errorMessage.includes('Script error')) {
      console.warn('Suppressed undefined/script error during template editing');
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
    
    console.error('🔥 UNHANDLED ERROR DETECTED:', event.error || event.message);
    
    if (
      errorMessage.includes('fire') || 
      errorMessage.includes('CustomEvents') ||
      errorMessage.includes('Cannot read properties of null') ||
      errorMessage.includes("reading 'fire'") ||
      errorStack.includes('CustomEvents') ||
      errorStack.includes('.fire(') ||
      errorStack.includes('tui-image-editor') ||
      errorStack.includes('toast-ui') ||
      errorStack.includes('vendors-node_modules_toast-ui') ||
      errorStack.includes('unlayer') ||
      (errorName === 'TypeError' && errorMessage.includes('null'))
    ) {
      // Suppress the error completely
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const errorMessage = reason?.message || String(reason) || '';
    const errorStack = reason?.stack || '';
    
    if (
      errorMessage.includes('fire') || 
      errorMessage.includes('CustomEvents') ||
      errorMessage.includes('Cannot read properties of null') ||
      errorMessage.includes("reading 'fire'") ||
      errorMessage.includes('tui-image-editor') ||
      errorMessage.includes('toast-ui') ||
      errorMessage.includes('vendors-node_modules_toast-ui') ||
      errorMessage.includes('unlayer') ||
      errorStack.includes('CustomEvents') ||
      errorStack.includes('tui-image-editor') ||
      errorStack.includes('toast-ui')
    ) {
      // Suppress the rejection
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  };

  // Use capture phase to catch errors early - add multiple listeners to ensure we catch it
  window.addEventListener('error', handleUnhandledError, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection, true);
  
  // Also override window.onerror as a fallback - this runs before event listeners
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    const errorMessage = String(message || '');
    const errorStack = error?.stack || '';
    const sourceString = String(source || '');
    
    if (
      errorMessage.includes('fire') ||
      errorMessage.includes('CustomEvents') ||
      errorMessage.includes('Cannot read properties of null') ||
      errorMessage.includes("reading 'fire'") ||
      errorStack.includes('CustomEvents') ||
      errorStack.includes('tui-image-editor') ||
      errorStack.includes('toast-ui') ||
      errorStack.includes('vendors-node_modules_toast-ui') ||
      sourceString.includes('toast-ui') ||
      sourceString.includes('tui-image-editor')
    ) {
      return true; // Suppress the error
    }
    
    if (originalOnError) {
      return originalOnError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };

  // Override window.onunhandledrejection as well
  const originalOnUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = (event) => {
    const reason = event.reason;
    const errorMessage = reason?.message || String(reason) || '';
    const errorStack = reason?.stack || '';
    
    if (
      errorMessage.includes('fire') ||
      errorMessage.includes('CustomEvents') ||
      errorMessage.includes('Cannot read properties of null') ||
      errorMessage.includes("reading 'fire'") ||
      errorStack.includes('CustomEvents') ||
      errorStack.includes('tui-image-editor') ||
      errorStack.includes('toast-ui') ||
      errorStack.includes('vendors-node_modules_toast-ui')
    ) {
      event.preventDefault();
      return true;
    }
    
    if (originalOnUnhandledRejection) {
      return originalOnUnhandledRejection.call(window, event);
    }
    return false;
  };
};

// Setup global error handlers before rendering
setupGlobalErrorHandlers();

// Patch React's error overlay to suppress ToastUI/Unlayer errors
if (process.env.NODE_ENV === 'development') {
  // Try to patch React's error overlay immediately and on DOM ready
  const patchReactErrorOverlay = () => {
    try {
      const windowWithOverlay = window as any;
      
      // Patch React error overlay global handler (used by react-error-overlay)
      if (windowWithOverlay.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__) {
        const originalHandler = windowWithOverlay.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__;
        windowWithOverlay.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__ = function(error: Error, isFatal?: boolean) {
          const errorMessage = error?.message || '';
          const errorStack = error?.stack || '';
          
          if (
            errorMessage.includes('fire') ||
            errorMessage.includes('CustomEvents') ||
            errorMessage.includes('Cannot read properties of null') ||
            errorMessage.includes("reading 'fire'") ||
            errorStack.includes('CustomEvents') ||
            errorStack.includes('tui-image-editor') ||
            errorStack.includes('toast-ui') ||
            errorStack.includes('vendors-node_modules_toast-ui') ||
            (error as any).__suppressed
          ) {
            // Completely suppress - don't show overlay
            return;
          }
          originalHandler(error, isFatal);
        };
      }
      
      // Also patch the error overlay if it's already mounted
      const overlay = document.getElementById('react-error-overlay-root');
      if (overlay) {
        // If overlay exists, try to hide it if it's showing ToastUI errors
        const observer = new MutationObserver(() => {
          const errorText = overlay.textContent || '';
          if (
            errorText.includes('fire') ||
            errorText.includes('CustomEvents') ||
            errorText.includes('Cannot read properties of null')
          ) {
            overlay.style.display = 'none';
          }
        });
        observer.observe(overlay, { childList: true, subtree: true });
      }
    } catch (e) {
      // Ignore if patching fails
    }
  };
  
  // Try immediately
  patchReactErrorOverlay();
  
  // Also try after DOM is ready and after a delay
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchReactErrorOverlay);
  }
  setTimeout(patchReactErrorOverlay, 100);
  setTimeout(patchReactErrorOverlay, 500);
}

// Check if root element exists
if (!document.getElementById('root')) {
  console.error('Root element not found!');
  document.body.innerHTML = '<div style="padding: 20px; font-family: sans-serif;"><h1>Error: Root element not found</h1><p>The application could not initialize because the root element is missing.</p></div>';
} else {
  // IMPORTANT: Keep public form pages lightweight by avoiding the full app import.
  const isPublicFormRoute =
    window.location.pathname.startsWith('/form/') ||
    window.location.pathname.startsWith('/confirm-subscription') ||
    window.location.pathname.startsWith('/unsubscribe');

  if (isPublicFormRoute) {
    const PublicSignupForm = require('./components/PublicSignupForm').default;
    const ConfirmSubscription = require('./components/ConfirmSubscription').default;
    const PublicUnsubscribe = require('./components/PublicUnsubscribe').default;

    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <Router>
            <Routes>
              <Route path="/form/:formId" element={<PublicSignupForm />} />
              <Route path="/confirm-subscription" element={<ConfirmSubscription />} />
              <Route path="/unsubscribe" element={<PublicUnsubscribe />} />
            </Routes>
          </Router>
        </ErrorBoundary>
      </React.StrictMode>
    );
  } else {
    const App = require('./App').default;
    const AdminApp = require('./AdminApp').default;
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          {(isAdminMode || isAdminSubdomain) ? <AdminApp /> : <App />}
        </ErrorBoundary>
      </React.StrictMode>
    );
  }
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Register service worker for caching static assets on repeat visits.
// CRA ships with a Workbox-based service worker; we enable it here.
// It caches the JS/CSS bundles with cache-first for hashed filenames and
// network-first for index.html, so repeat visits feel nearly instant.
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {
      // Fail silently — service worker is a progressive enhancement
    });
  });
}
